/**
 * 材料价格配置API服务
 */

// 生产环境使用相对路径，开发环境使用localhost
// Use relative path in production, localhost in development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface MaterialPrice {
  _id: string;
  materialName: string;
  unit: string;
  price: number;
  category?: string;
  effectiveDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface MaterialPriceCreateData {
  materialName: string;
  unit: string;
  price: number;
  category?: string;
  effectiveDate?: string;
}

export interface MaterialPriceUpdateData {
  materialName?: string;
  unit?: string;
  price?: number;
  category?: string;
  effectiveDate?: string;
}

export interface MaterialPriceListParams {
  page?: number;
  limit?: number;
  materialName?: string;
  category?: string;
  unit?: string;
}

export interface MaterialPriceListResponse {
  success: boolean;
  data: MaterialPrice[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: any[];
}

class MaterialPriceService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`API请求失败 [${endpoint}]:`, error);
      throw error;
    }
  }

  /**
   * 获取材料价格列表
   */
  async getMaterialPrices(params: MaterialPriceListParams = {}): Promise<MaterialPriceListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.materialName) searchParams.append('materialName', params.materialName);
    if (params.category) searchParams.append('category', params.category);
    if (params.unit) searchParams.append('unit', params.unit);

    const queryString = searchParams.toString();
    const endpoint = `/material-prices${queryString ? `?${queryString}` : ''}`;

    return this.request<MaterialPriceListResponse>(endpoint);
  }

  /**
   * 根据ID获取材料价格详情
   */
  async getMaterialPriceById(id: string): Promise<ApiResponse<MaterialPrice>> {
    return this.request<ApiResponse<MaterialPrice>>(`/material-prices/${id}`);
  }

  /**
   * 创建材料价格配置
   */
  async createMaterialPrice(data: MaterialPriceCreateData): Promise<ApiResponse<MaterialPrice>> {
    return this.request<ApiResponse<MaterialPrice>>('/material-prices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * 更新材料价格配置
   */
  async updateMaterialPrice(id: string, data: MaterialPriceUpdateData): Promise<ApiResponse<MaterialPrice>> {
    return this.request<ApiResponse<MaterialPrice>>(`/material-prices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * 删除材料价格配置
   */
  async deleteMaterialPrice(id: string): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/material-prices/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * 获取材料建议列表
   */
  async getMaterialSuggestions(query?: string): Promise<ApiResponse<string[]>> {
    const searchParams = new URLSearchParams();
    if (query) searchParams.append('q', query);
    
    const queryString = searchParams.toString();
    const endpoint = `/material-prices/suggestions${queryString ? `?${queryString}` : ''}`;

    return this.request<ApiResponse<string[]>>(endpoint);
  }

  /**
   * 获取常用单位列表
   */
  async getCommonUnits(): Promise<ApiResponse<string[]>> {
    return this.request<ApiResponse<string[]>>('/material-prices/units');
  }

  /**
   * 获取材料分类列表
   */
  async getCategories(): Promise<ApiResponse<string[]>> {
    return this.request<ApiResponse<string[]>>('/material-prices/categories');
  }

  /**
   * 检查价格冲突
   */
  async checkPriceConflicts(materials: Array<{
    materialName: string;
    specification?: string;
    unit: string;
    unitPrice: number;
    usageDate: string;
  }>): Promise<ApiResponse<{
    conflicts: Array<{
      index: number;
      materialName: string;
      specification?: string;
      unit: string;
      existingPrice: number;
      existingPriceId: string;
      existingEffectiveDate: string;
      newPrice: number;
      newEffectiveDate: string;
    }>;
    toCreate: Array<{
      index: number;
      materialName: string;
      specification?: string;
      unit: string;
      price: number;
    }>;
    existing: Array<{
      index: number;
      priceId: string;
    }>;
  }>> {
    return this.request('/material-prices/check-conflicts', {
      method: 'POST',
      body: JSON.stringify({ materials })
    });
  }
}

// 创建服务实例
export const materialPriceService = new MaterialPriceService();
export default materialPriceService;